import React, { useEffect, useRef, useState } from 'react';
import ManagedSources from './ManagedSources';
import { Switch, Route, useRouteMatch, useLocation } from 'react-router-dom';
import { getManagedSources } from './LandscapeUtils';
import { startManagedLandscapeDiscovery } from './manage-api';
import FetchContainer from 'Utils/FetchContainer';

const AssetManager = (props) => {
  const [fetchingData, setFetchingData] = useState(false);
  const [timestamp, setTimestamp] = useState(0);
  const [managedSources, setManagedSources] = useState([]);
  const [error, setError] = useState(null);
  const location = useLocation();
  const { path } = useRouteMatch();

  // On mount, read whatever the manager already has on disk. The backend now
  // schedules a full landscape refresh every 60 minutes and runs targeted
  // per-resource polls after start/stop actions, so the UI does not need to
  // kick off a discovery on page load.
  useEffect(() => {
    loadFromManager();
  }, []);

  // Polling cadence for the post-refresh wait. Kept deliberately slow: the
  // manager serves the landscape as a chunked response, and hammering it every
  // few seconds over a reused keep-alive connection can yield an empty body
  // through the reverse proxy. 10s is plenty for a discovery that takes tens of
  // seconds, and the total window (interval * attempts) still covers a long scan.
  const POLL_INTERVAL_MS = 10000;
  const POLL_MAX_ATTEMPTS = 18;

  // Reconcile cadence for the post-action wait. After a start/stop the manager writes the
  // transitional status ('starting'/'stopping') into its landscape at once, then polls the
  // resource's cloud API every 3 minutes until it settles to 'running'/'stopped' and patches
  // that in. The UI mirrors that: one quick read so the row reflects the action, then a slow
  // poll that only stops once nothing is mid-transition (bounded to the manager's 20-minute
  // watch ceiling so a wedged resource can't poll forever). Polling faster than the manager's
  // own cadence just re-reads the same status (C2D-1001).
  const RECONCILE_FIRST_DELAY_MS = 3000;
  const RECONCILE_INTERVAL_MS = 30000;
  const RECONCILE_MAX_ATTEMPTS = 40;
  const TRANSITIONAL_STATUSES = ['starting', 'stopping', 'pending'];
  const reconciling = useRef(false);

  // Called by the table right after a start/stop action fires. Guarded so overlapping toggles
  // share one loop rather than stacking timers on the chunked landscape endpoint.
  const reconcileAfterAction = async () => {
    if (reconciling.current) return;
    reconciling.current = true;
    try {
      for (let attempt = 0; attempt < RECONCILE_MAX_ATTEMPTS; attempt += 1) {
        await wait(attempt === 0 ? RECONCILE_FIRST_DELAY_MS : RECONCILE_INTERVAL_MS);
        const landscape = await getManagedSources();
        if (landscape && Array.isArray(landscape.sources) && landscape.sources.length > 0) {
          setManagedSources(landscape.sources);
          setTimestamp(landscape.time_stamp || 0);
          const stillMoving = landscape.sources.some((s) => TRANSITIONAL_STATUSES.includes(s.status));
          if (!stillMoving) break;
        }
      }
    } catch (e) {
      console.log('reconcileAfterAction error', e);
    } finally {
      reconciling.current = false;
    }
  };

  const loadFromManager = async () => {
    try {
      const landscape = await getManagedSources();
      // Don't clobber already-displayed resources with an empty/failed read
      // (a transient empty body from the proxy must not blank the table).
      if (landscape && Array.isArray(landscape.sources) && landscape.sources.length > 0) {
        setManagedSources(landscape.sources);
        setTimestamp(landscape.time_stamp || 0);
      } else if (managedSources.length === 0) {
        // First load with genuinely nothing yet — render the empty state.
        setManagedSources([]);
        setTimestamp(landscape && landscape.time_stamp ? landscape.time_stamp : 0);
      }
    } catch (e) {
      console.log('loadFromManager error', e);
      setError(e && e.message ? e.message : 'Failed to load landscape');
    }
  };

  // Manual refresh (user clicked "Refresh Resources"). Kicks off a full
  // discovery, then polls until the timestamp advances past what we last saw.
  const refreshLandscapeHandler = async () => {
    try {
      setFetchingData(true);
      const baseline = timestamp;
      await startManagedLandscapeDiscovery();

      const result = await poll(
        () => getManagedSources(),
        // Keep polling until we get a newer snapshot that actually has data.
        // An empty/zero-timestamp result is a transient proxy read, not a result.
        (res) => !res || (res.time_stamp || 0) <= baseline || !(res.sources && res.sources.length),
        POLL_INTERVAL_MS
      );
      // Only adopt the new snapshot if it advanced and carries data; otherwise
      // leave the existing resources on screen.
      if (result && (result.time_stamp || 0) > baseline && result.sources && result.sources.length) {
        setManagedSources(result.sources);
        setTimestamp(result.time_stamp);
      }
    } catch (e) {
      console.log(e);
      setError(e && e.message ? e.message : 'Failed to refresh landscape');
    } finally {
      setFetchingData(false);
    }
  };

  const poll = async function (fn, fnCondition, ms) {
    let result = await fn();
    let attempts = 0;
    while (fnCondition(result) && attempts < POLL_MAX_ATTEMPTS) {
      await wait(ms);
      result = await fn();
      attempts += 1;
    }
    return result;
  };

  const wait = function (ms = 15000) {
    return new Promise((resolve) => setTimeout(resolve, ms));
  };

  return (
    <FetchContainer fetchingData={false} absolute={true} error={error}>
      <div className='w-100'>
        <Switch>
          {/* Cloud Landscape opens straight to the resource list (the pie-chart
              overview is bypassed). The optional :source segment still deep-links
              a category / source / region filter for links elsewhere in the app. */}
          <Route exact path={`${path}`}>
            <ManagedSources
              fetchingData={fetchingData}
              managedSources={managedSources}
              pageSize={10}
              refreshStatus={() => refreshLandscapeHandler()}
              onActionStarted={reconcileAfterAction}
            />
          </Route>
          <Route exact path={`${path}/:source`}>
            <ManagedSources
              fetchingData={fetchingData}
              managedSources={managedSources}
              pageSize={10}
              refreshStatus={() => refreshLandscapeHandler()}
              onActionStarted={reconcileAfterAction}
            />
          </Route>
        </Switch>
      </div>
    </FetchContainer>
  );
};

export default AssetManager;
